import type { IconProps } from "@octopusdeploy/design-system-icons";
import { ArrowUpIcon, ExclamationCircleIcon, GhostIcon, HeartCrackIcon, HeartIcon, HourglassIcon, HourglassXmarkIcon, QuestionMarkIcon, SpinnerIcon } from "@octopusdeploy/design-system-icons";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type React from "react";
import type { BaseStatuses, BaseStatusSize } from "./BaseStatus";
import { BaseStatus } from "./BaseStatus";
export const liveStatuses = ["progressing", "healthy", "unknown", "degraded", "missing", "status-unavailable", "waiting", "stale", "out-of-sync"] as const;
export type LiveStatusType = (typeof liveStatuses)[number];
export interface LiveStatusProps {
    /**
     * The live status state.
     */
    status: LiveStatusType;
    /**
     * The size of the status badge.
     */
    size: BaseStatusSize;
    /**
     * Optional tooltip text to display on hover.
     */
    tooltip?: string;
}
export function LiveStatus({ status, size, tooltip }: LiveStatusProps) {
    const { icon, backgroundStatus, iconVariant, label, spinning } = getStatusDetails(status);
    return <BaseStatus icon={icon} status={backgroundStatus} iconVariant={iconVariant} label={label} size={size} spinning={spinning} tooltip={tooltip}/>;
}
interface LiveStatusDetails {
    icon: React.ComponentType<IconProps>;
    backgroundStatus: BaseStatuses;
    iconVariant: "default" | "inverse";
    label: string;
    spinning: boolean;
}
function getStatusDetails(status: LiveStatusType): LiveStatusDetails {
    switch (status) {
        case "progressing":
            return {
                icon: SpinnerIcon,
                backgroundStatus: "executing",
                iconVariant: "default",
                label: "Progressing",
                spinning: true,
            };
        case "healthy":
            return {
                icon: HeartIcon,
                backgroundStatus: "success",
                iconVariant: "default",
                label: "Healthy",
                spinning: false,
            };
        case "unknown":
            return {
                icon: QuestionMarkIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Unknown",
                spinning: false,
            };
        case "degraded":
            return {
                icon: HeartCrackIcon,
                backgroundStatus: "failure",
                iconVariant: "default",
                label: "Degraded",
                spinning: false,
            };
        case "missing":
            return {
                icon: GhostIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Missing",
                spinning: false,
            };
        case "status-unavailable":
            return {
                icon: ExclamationCircleIcon,
                backgroundStatus: "failure",
                iconVariant: "default",
                label: "Status unavailable",
                spinning: false,
            };
        case "waiting":
            return {
                icon: HourglassIcon,
                backgroundStatus: "executing",
                iconVariant: "default",
                label: "Waiting",
                spinning: false,
            };
        case "stale":
            return {
                icon: HourglassXmarkIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Stale",
                spinning: false,
            };
        case "out-of-sync":
            return {
                icon: ArrowUpIcon,
                backgroundStatus: "neutral",
                iconVariant: "inverse",
                label: "Out of sync",
                spinning: false,
            };
        default:
            return exhaustiveCheck(status, `Unexpected status ${status} for LiveStatusType`);
    }
}
